import { useState } from 'preact/hooks';
import { CalendarClockIcon, CheckIcon, WalletIcon } from 'lucide-react';
import { formatUsd, minDownPaymentLabel } from 'shared';
import { AppShell } from '@/flows/marketplace/components/app-shell.js';
import { BackLink } from '@/flows/marketplace/components/back-link.js';
import { Button } from '@/components/button.js';
import { Card } from '@/flows/marketplace/components/card.js';
import { FieldFrame } from '@/components/field.js';
import { OrderSummary } from '@/flows/marketplace/components/order-summary.js';
import { PageHero } from '@/flows/marketplace/components/page-hero.js';
import { PriceDisplay } from '@/components/price-display.js';
import { ProgressBar } from '@/flows/marketplace/components/progress-bar.js';
import { OptionList } from '@/flows/marketplace/components/option-list.js';
import { groupsNotInCart } from '@/lib/group-rated-products.js';
import { defaultDownPaymentInput, monthlyEstimate } from '@/lib/finance.js';
import { financeLimits } from '@/state/config.js';
import { removeCartEntry, setCartEntryPayment } from '@/state/session.js';
import { findCartLine } from '@/state/pricing.js';
import type { CartLine } from '@/lib/widget-types.js';
import { activeCartIndex, paymentOrigin, step } from '@/flows/marketplace/state/step.js';

/** How the customer pays for ONE product — nothing here talks about the order as a whole. */
export function Payment() {
  const index = activeCartIndex.value;
  const line = index === null ? undefined : findCartLine(index);
  // The entry was removed on the way out and `step` is about to follow — render nothing
  // for that one frame, like a product card whose tier is gone.
  if (index === null || line === undefined) return null;

  // Keyed by cart index so switching products resets the error-reveal state with it.
  return <ProductPayment key={index} index={index} line={line} />;
}

function ProductPayment({ index, line }: { index: number; line: CartLine }) {
  const [showDownPaymentError, setShowDownPaymentError] = useState(false);

  const { entry, tier, durationOptions, canFinance, minDown, monthly } = line;
  const isFinance = entry.payment.type === 'finance';
  const downPaymentErrorText = showDownPaymentError ? line.downPaymentError : null;
  const longestTerm = durationOptions[durationOptions.length - 1];
  // `monthly` is only set once the down payment is one we'd submit.
  const estimateDown = monthly === null ? null : line.downPayment;

  function selectPayInFull() {
    setCartEntryPayment(index, { type: 'pay_in_full' });
    setShowDownPaymentError(false);
  }

  function selectFinance() {
    if (longestTerm === undefined) return;
    setCartEntryPayment(index, {
      type: 'finance',
      durationMonths: longestTerm,
      downPaymentInput: defaultDownPaymentInput(line.financeWholesale, financeLimits.value),
    });
    setShowDownPaymentError(false);
  }

  function handleDurationChange(durationMonths: number) {
    if (entry.payment.type !== 'finance') return;
    setCartEntryPayment(index, { ...entry.payment, durationMonths });
  }

  function handleDownPaymentInput(downPaymentInput: string) {
    if (entry.payment.type !== 'finance') return;
    setCartEntryPayment(index, { ...entry.payment, downPaymentInput });
    setShowDownPaymentError(false);
  }

  function handleBack() {
    // Choosing a plan and choosing how to pay for it is one action, so backing out of a
    // fresh add takes the plan with it; an edit from the summary just returns.
    if (paymentOrigin.value === 'tiers') {
      // Step first: dropping the entry while this screen is still mounted would render it
      // against an index that no longer exists.
      step.value = 'tiers';
      removeCartEntry(index);
      return;
    }
    step.value = 'customer-info';
  }

  function handleContinue() {
    if (line.downPaymentError !== null) {
      setShowDownPaymentError(true);
      return;
    }
    // Add-another only offers more plans, so it's skipped once none are left.
    step.value =
      paymentOrigin.value === 'tiers' && groupsNotInCart().length > 0
        ? 'add-another'
        : 'customer-info';
  }

  return (
    <AppShell>
      <BackLink onClick={handleBack} />

      <PageHero
        title={`How would you like to pay for ${tier.product_name}?`}
        subtitle={
          canFinance
            ? 'Pay in full today, or spread the cost over monthly payments.'
            : 'This plan is short enough that it has to be paid in full.'
        }
      />

      <ProgressBar />

      <Card>
        <div className="sm-stack sm-form">
          <div className="sm-stack sm-section">
            <p className="sm-type-eyebrow">Payment method</p>
            <div className="sm-row-2-stack">
              <PayTypeCard
                icon={WalletIcon}
                title="Pay in full"
                subtitle={`${formatUsd(line.pifWholesale)} — one charge today, no payments after.`}
                selected={!isFinance}
                onClick={selectPayInFull}
              />
              {canFinance && (
                <PayTypeCard
                  icon={CalendarClockIcon}
                  title="Finance"
                  subtitle={`${formatUsd(line.financeWholesale)} — spread over up to ${longestTerm} months.`}
                  selected={isFinance}
                  onClick={selectFinance}
                />
              )}
            </div>
          </div>

          {entry.payment.type === 'finance' && (
            <div className="sm-stack sm-section">
              <p className="sm-type-eyebrow">Finance details</p>
              <div className="sm-stack sm-finance-block">
                <FieldFrame
                  label="Down payment"
                  labelAux="Minimum"
                  htmlFor="sm-down-payment"
                  error={downPaymentErrorText ?? undefined}
                  help={`At least ${formatUsd(minDown)} (${minDownPaymentLabel(financeLimits.value)}) is required to finance. Anything more reduces the balance.`}
                >
                  <div className="sm-input-wrap">
                    <span className="sm-type-body sm-input-currency-prefix">$</span>
                    <input
                      id="sm-down-payment"
                      type="text"
                      className="sm-control sm-input sm-input-currency"
                      inputMode="decimal"
                      autoComplete="off"
                      aria-invalid={downPaymentErrorText !== null || undefined}
                      value={entry.payment.downPaymentInput}
                      onInput={(e) =>
                        handleDownPaymentInput((e.currentTarget as HTMLInputElement).value)
                      }
                    />
                  </div>
                </FieldFrame>

                <FieldFrame label="Payment duration">
                  <OptionList<number>
                    ariaLabel="Payment duration"
                    value={entry.payment.durationMonths}
                    options={durationOptions.map((m) => ({
                      value: m,
                      label: `${m} months`,
                      detail:
                        estimateDown === null
                          ? undefined
                          : `${formatUsd(monthlyEstimate(line.total, estimateDown, m))}/mo`,
                    }))}
                    onChange={handleDurationChange}
                  />
                </FieldFrame>

                {monthly !== null && (
                  <div className="sm-monthly-preview sm-monthly-preview-stack">
                    <span className="sm-type-eyebrow">Estimated monthly payment</span>
                    <span className="sm-type-price sm-amount">
                      <PriceDisplay amount={monthly} unit="/mo" />
                    </span>
                    <span className="sm-type-caption">
                      for {entry.payment.durationMonths} months · {formatUsd(line.downPayment ?? 0)}{' '}
                      down
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="sm-stack sm-cta-block">
            <Button onClick={handleContinue}>
              {paymentOrigin.value === 'customer-info' ? 'Save changes' : 'Continue'}
            </Button>
          </div>
        </div>
      </Card>

      <OrderSummary />
    </AppShell>
  );
}

interface PayTypeCardProps {
  icon: typeof WalletIcon;
  title: string;
  subtitle: string;
  selected: boolean;
  onClick: () => void;
}

function PayTypeCard({ icon: Icon, title, subtitle, selected, onClick }: PayTypeCardProps) {
  return (
    <button
      type="button"
      className="sm-focus-ring sm-paytype"
      aria-pressed={selected}
      onClick={onClick}
    >
      <span className="sm-center sm-paytype-icon">
        <Icon size={18} aria-hidden="true" />
      </span>
      <span className="sm-stack sm-pair sm-paytype-text">
        <span className="sm-type-body-strong">{title}</span>
        <span className="sm-type-body sm-paytype-sub">{subtitle}</span>
      </span>
      <span className="sm-center sm-paytype-check">
        <CheckIcon size={12} strokeWidth={3} aria-hidden="true" />
      </span>
    </button>
  );
}
